import { extendTheme } from '@chakra-ui/react'

/**
 * 天籟接送系統 — 共用配色主題（家長 App / 老師手機 PWA / 桌面控制台 三端共用）
 *
 * 設計方向：「森綠 × 陽光黃」（Forest Green on Sunshine）— 2026-08-07 依 user 指定改版。
 *   主色 = 深森林綠（取自補習班 logo 的松鼠與外框），底色 = 柔和陽光黃，卡片 = 米白。
 *   徽章 = 磚紅。user 指定的四個色號逐字採用，其餘色階是由它們推導並經對比度驗證的。
 *
 * ───────────────────────────────────────────────────────────────────────────
 * 一、表面系統（surface ladder）
 *
 * 這一版有三層表面，而且「畫布」與「凹槽」是分開的兩件事（見下方「二」）：
 *
 *   主要卡片          white         #FAF6EE   L*97.0   ← user 指定
 *   狀態卡 / 凹槽     neutral.50    #F5F0E6   L*94.9   ← user 指定，與主卡差 ΔL*2.1
 *   App 畫布          canvas        #FDE78C   L*91.7   ← user 指定，與主卡差 ΔL*5.3
 *   分隔線            neutral.200   #E3DACA   L*87.4
 *
 * 卡片對畫布 ΔL*5.3，卡片確實浮在黃底上；主卡對狀態卡 ΔL*2.1，是「同一層裡的次級區塊」
 * 的細微差別，剛好對應 user 指定的兩種卡片底色。
 *
 * 三種分隔手法各司其職：
 *   色塊（tone）  → 分組：卡片 vs 畫布
 *   邊框（1px）   → 同一表面內的邊界：表格列線、清單分隔、輸入框邊界
 *   陰影（shadow）→ 立體感。**本版依 user 要求恢復卡片陰影**：低透明度、大模糊、
 *                   柔和擴散，讓卡片「懸浮」在黃色畫布上。上一版取消卡片陰影是因為
 *                   當時地與卡的明度差已足以分組；改成黃底之後，柔和陰影能讓米白卡片
 *                   的邊界在彩色背景上更明確。
 *
 * ───────────────────────────────────────────────────────────────────────────
 * 二、⚠ 畫布（黃）與凹槽（米）必須分開 — 這一版最容易踩錯的地方
 *
 * 舊版把 App 底色與凹槽色都指給 neutral.50。改成黃色主題時**不能照做**：
 * 全專案有 50 處 bg="gray.50"，用途是「凹槽」——搜尋框底、卡片內區塊、ModalBody、
 * Thead。若把 neutral.50 設成黃色，這些地方會全部變黃，但參考設計裡黃色只有頁面底層。
 *
 * 因此：
 *   canvas（新增）→ #FDE78C 黃，只給「頁面畫布」：styles.global.body、
 *                   MobileLayout 外殼、index.css 的 overscroll 底色
 *   neutral.50    → #F5F0E6 米，維持「凹槽 / 狀態卡」語意，50 處用法不必改
 *
 * 其餘沿用上一版的做法：直接覆寫 Chakra 內建色票，讓元件一行都不用改。
 * 本專案畫面上的顏色大部分不是主題決定的（bg="white" 99 次、bg="gray.50" 50 次、
 * gray.* 約 483 次、blue.* 79 次 + colorScheme="blue" 38 次都吃 Chakra 預設值），
 * 覆寫內建色票才能一次收斂。
 *
 * 對應關係：
 *   bg="white"     → 主要卡片、App bar、側欄、清單列
 *   bg="gray.50"   → 卡片內凹槽、ModalBody、Thead、狀態卡
 *   bg="gray.100"  → 表頭填色、圖片佔位、填色輸入框
 *
 * ───────────────────────────────────────────────────────────────────────────
 * 三、色階規則
 *
 *   50–200  = 表面與分隔線（不承載文字資訊）
 *   300     = 功能性邊框、圖示（對三種表面皆 ≥ 3:1，WCAG 1.4.11）
 *   400–900 = 文字安全區（對三種表面皆 ≥ 4.5:1）
 *
 * 這條規則是照實際程式碼稽核訂的：color="gray.400" 有 51 處當內文用，
 * 舊值 #A0AEC0 對白底只有 2.26:1，直接違反 WCAG AA。
 *
 * 註：三端目前皆鎖 light mode（index.css 有 color-scheme: light），
 *     故此處不定義 dark mode 色票，避免產生永遠不會被使用的半成品。
 *
 * ⚠ 已知限制：index.css 的 body { background-color: #ffffff !important }
 *   會蓋掉下方 styles.global 的 App 底色。主要畫面因為 AdminLayout /
 *   ParentLayout 自己有設 bg 所以不受影響，但手機端下拉回彈（overscroll）
 *   會露出純白。需要移除該行 !important 才會完全一致。
 */

// ---------------------------------------------------------------------------
// App 畫布：霧灰橄欖（user 2026-08-13 指定 #8A9A7B，取代原本的陽光黃 #FDE78C）。
// **只給頁面底層**，不要拿它當凹槽色 —— 理由見檔案開頭「二」。
//
// ⚠️ 換色帶來的結構性影響（務必先讀完再改任何東西）：
// 舊畫布是亮色（L*91.7），所以「深色 neutral 階壓在畫布上」天然成立。
// 新畫布是**中間調**（L*61.6），這件事不再成立 —— 實測：
//     neutral.400 #686157（弱化文字，51 處）  對比 2.03  ✗
//     neutral.500 #635C53（次要文字，112 處）  對比 2.29  ✗
//     neutral.800 #2D2822（標題）              對比 4.86  ✓
//     純白 #FFFFFF                             對比 3.00  ✗（AA 小字需 4.5）
// 也就是說中間調背景兩邊都難討好：淺色與中灰都不夠，只有近黑或純白（大字）勉強。
//
// 因此**畫布上的前景另立一套 token（下方 onCanvas）**，不要再直接用 neutral 階。
// neutral 階仍然正確——但它的適用範圍已經縮小為「卡片內部」（卡片底 #FAF6EE 沒變）。
// ---------------------------------------------------------------------------
const CANVAS = '#8A9A7B'

// ---------------------------------------------------------------------------
// 畫布上的前景（**只用於直接壓在畫布上的文字與圖示**；卡片內部一律維持 neutral 階）。
//
// 【為什麼是深色字而不是白字 —— 2026-08-13 依 user「文字可視度 OK」的要求改】
// 這個畫布是中間調（L*61.6），兩個方向都試算過：
//     白字 100% / 85% / 72%   →  3.00 / 2.61 / 2.30   三階全部未達 AA 小字 4.5
//     近黑 100% / 92% / 84%   →  5.83 / 5.22 / 4.58   三階全部達標
// 白字方案在此底色上無法達標（純白已是上限），故改用深色。
//
// 三階的不透明度不是憑感覺挑的：以二分法算出「在此底色上仍達 AA(4.5) 的最低不透明度
// = 0.830」，三階都設在其上。換底色時務必重算這個下限，不要沿用這三個數字。
//
// ⚠️ 層次空間仍然有限（三階看起來都是深色）。**畫面層次主要來自字級與字重**
//（元件本來就有：16px 粗標題／12px 日期／10px 時間），不透明度只是輕微收斂。
// 要再拉開層次請動字級字重，降到 0.83 以下就掉出 AA。
//
// 要換回白字方案時只需改下面三個值、不必動任何元件：
//     500: '#FFFFFF' / 400: 'rgba(255,255,255,0.85)' / 300: 'rgba(255,255,255,0.72)'
//     —— 但請記得那組在此底色上是不達標的（3.00 / 2.61 / 2.30）。
// ---------------------------------------------------------------------------
const onCanvas = {
    500: '#1C1914',                 // 主要：頁面標題、導航列選中項      5.83
    400: 'rgba(28, 25, 20, 0.92)',  // 次要：日期、區塊標題、發話者名稱  5.22
    300: 'rgba(28, 25, 20, 0.84)',  // 弱化：導航列未選中、訊息時間      4.58
    line: 'rgba(28, 25, 20, 0.20)', // 畫布上的分隔線
}

// ---------------------------------------------------------------------------
// 表面：米白。同時指派給 neutral 與 Chakra 內建的 gray。
// 50 是 user 指定的狀態卡底色，往下的階是依它推導、與黃色畫布同色溫的暖米。
// 300 以上的對比值標註「黃/主卡/狀態卡」三種表面的最差情形（三者都要達標，
// 因為同一個 token 可能出現在任一層上）。
// ---------------------------------------------------------------------------
const neutral = {
    50: '#F5F0E6',  // 狀態卡、卡片內凹槽、ModalBody、Thead   L*94.9（user 指定）
    100: '#EDE6D9', // 次要填色、表頭、hover、圖片佔位        L*92.4
    200: '#E3DACA', // 分隔線、表格列線、輸入框邊界           L*87.4
    300: '#837B71', // 功能性邊框、搜尋圖示   最差 3.37（需 3.0）
    400: '#686157', // 弱化文字（51 處）      最差 4.94（需 4.5）
    500: '#635C53', // 次要文字（112 處）     最差 5.33
    600: '#4F4941', // 內文                   最差 7.19
    700: '#3E3831', // 強調內文               最差 9.35
    800: '#2D2822', // 標題                   最差 11.81
    900: '#1C1914', // 近黑（非純黑）         最差 14.16
}

// ---------------------------------------------------------------------------
// 品牌色：深森林綠。500 = #1E4D2B 為 user 指定，取自補習班 logo 的松鼠與外框線條。
// 用途：logo、底部導覽列圖示、功能按鈕圖示、鈴鐺圖示、主要動作按鈕。
// 白字在 brand.500 上有 9.76:1，是這套配色裡對比最充裕的組合。
// 淺階（50–300）刻意降飽和度往米色靠，讓它們跟黃畫布/米卡片是同一個色溫家族，
// 而不是直接把綠打淡（那樣會出現螢光薄荷綠，跟整體調性打架）。
// 同時指派給 Chakra 的 blue / cyan，讓既有 colorScheme="blue" 的元件自動對齊。
// ---------------------------------------------------------------------------
const brand = {
    50: '#EDF4EE',  // 選中底、淺標籤底
    100: '#D8E7DB',
    200: '#B3CDB8',
    300: '#64846B', // 功能性邊框/圖示  最差 3.36（需 3.0）
    400: '#2E6B3E', // 次級動作         白字 6.39
    500: '#1E4D2B', // 主要動作/連結/圖示  白字 9.76、當文字最差 7.89（user 指定）
    600: '#173D22', // hover
    700: '#122F1A', // active
    800: '#0E2414', // 品牌標題
    900: '#0A190E',
}

// ---------------------------------------------------------------------------
// 語意色：沿用原本的色相語意（紅=危險、橘=提醒、綠=完成、紫=分類、青=次要動作）。
//
// .50 / .100 兩個淺階是「狀態底色」，做法是把 App 底色的暖度保留一部分再加上
// 該色相的彩度，並把明度對齊中性表面（.100 ≈ L*92 = neutral.100）。
// 這樣色塊是「同一張紙上染了色」，不會像貼紙一樣浮在版面上。
//
// .400–.900 則往下壓到「白字實心按鈕」與「白底彩色文字」兩種用法都能過 AA。
// Chakra 內建值在本專案的實際用法下大量不合格，例如：
//   red.500  #E53E3E  4.13:1（16 處當文字）   green.500 #38A169 3.25:1（8 處）
//   orange.500 #DD6B20 3.39:1（5 處）         teal.500  #319795 3.51:1
// ---------------------------------------------------------------------------
// red.500 = #C0392B 為 user 指定的徽章色（未讀訊息、通知紅點）。
// 主要用法是「白字在紅底上」＝ 5.44:1，達標。
// ⚠ 已知邊際：紅色**當文字**時，在黃畫布上是 4.40:1（差 0.1 未達 AA）；在兩種卡片上
//   則是 5.05 / 4.79 皆達標。專案內 color="red.*" 的文字都落在卡片內，故實務上不受影響。
//   若日後真要把紅字直接放在黃畫布上，請改用 red.600（最差 6.08，充裕）。
const red = {
    50: '#FBEAE8', 100: '#F6D2CE', 200: '#EBA9A1', 300: '#DC8076',
    400: '#C24F42', // 白字 4.68（原推導值 #CE5346 只有 4.24，已壓深）
    500: '#C0392B', // 徽章底（user 指定）  白字 5.44
    600: '#9C2C21', // 紅字安全值           最差 6.08
    700: '#7C231A', 800: '#611B14', 900: '#47130E',
}

const orange = {
    50: '#FFF0E0', 100: '#FFE3C5', 200: '#F2CE9A', 300: '#E3AC5F',
    // 500 比第一版再壓深 2.5 個 L*：App 底變暗之後，舊值在底色上只有 4.41:1
    400: '#B07013', 500: '#955B08', 600: '#7F4E0E', 700: '#663E0B',
    800: '#533209', 900: '#3E2607',
}

const green = {
    50: '#E6F7E9', 100: '#CCF1D8', 200: '#ABD9BE', 300: '#78BF99',
    400: '#3F8F65', 500: '#2E7553', 600: '#255E43', 700: '#1E4B36',
    800: '#183D2C', 900: '#122E21',
}

const teal = {
    50: '#E0F8F7', 100: '#BEF2F4', 200: '#A5D8DC', 300: '#6DBEC4',
    400: '#2C8B93', 500: '#1F7178', 600: '#195B61', 700: '#14494E',
    800: '#113C40', 900: '#0C2D30',
}

// 紫色刻意壓低彩度、往靛紫靠，避免變成螢光「AI 紫」
const purple = {
    50: '#F8F0FF', 100: '#EFE3FF', 200: '#C8C2E7', 300: '#A79ED6',
    400: '#7566B8', 500: '#5D4F9B', 600: '#4B407E', 700: '#3D3465',
    800: '#322B53', 900: '#251F3E',
}

// yellow 的淺階色相刻意往綠側偏 14°，否則 yellow.100 與 orange.100 的
// 色差只有 ΔE 3.9（會被誤認為同一個狀態）；偏移後是 ΔE 10.0。
const yellow = {
    50: '#FAF3DA', 100: '#F3E9BC', 200: '#F0DA8C', 300: '#DCBE4C',
    400: '#A8830C', 500: '#8E6E0A', 600: '#735808', 700: '#5C4706',
    800: '#4A3905', 900: '#372B04',
}

// 表面語意別名。既有元件用的是 white / gray.50 / gray.100，
// 這組是給新程式碼用的，讓「這是什麼層級」寫在程式碼裡而不是靠記憶。
const surface = {
    canvas: CANVAS,        // App 畫布（黃）— 只給頁面底層，勿當凹槽用
    sunken: neutral[50],   // 凹槽、狀態卡
    muted: neutral[100],   // 次要填色、表頭
    default: '#FAF6EE',    // 主要卡片（user 指定）
    line: neutral[200],    // 分隔線
}

// 陰影：幾何沿用 Chakra 預設（不改尺寸），黑色換成暖墨色，否則中性黑陰影落在暖色面上會發灰。
// 另依 user 要求新增 card：低透明度、大模糊、柔和擴散，讓米白卡片「懸浮」在黃色畫布上。
const INK = '28, 25, 20' // = neutral.900

const theme = extendTheme({
    config: {
        initialColorMode: 'light',
        useSystemColorMode: false,
    },
    colors: {
        // 卡片表面。覆寫 Chakra 的 white，讓 99 處 bg="white" 自動吃到，
        // 同時符合「不用純白」的原則（color="white" 的文字對比僅差 1%，見報告）。
        white: surface.default,

        // 畫布色也註冊成頂層 token，index.css 才能用 var(--chakra-colors-canvas)
        // 讓手機下拉回彈露出的底色與 App 畫布一致。
        canvas: CANVAS,

        // 畫布上的前景。只給「直接壓在畫布上」的文字/圖示用；
        // 卡片內部一律繼續用 neutral 階（卡片底 #FAF6EE 未變，那套對比仍然成立）。
        onCanvas,

        brand,
        neutral,
        surface,

        // 只有一個強調色：accent 與 cyan 一律指回 brand，
        // 讓未來的人即使誤用也不會憑空生出第二個強調色。
        accent: brand,
        cyan: brand,

        // 覆寫 Chakra 內建色票，讓既有元件自動對齊同一套系統
        gray: neutral,
        blue: brand,
        red,
        orange,
        green,
        teal,
        purple,
        yellow,
    },
    shadows: {
        xs: `0 0 0 1px rgba(${INK}, 0.05)`,
        sm: `0 1px 2px 0 rgba(${INK}, 0.06)`,
        base: `0 1px 3px 0 rgba(${INK}, 0.10), 0 1px 2px 0 rgba(${INK}, 0.06)`,
        md: `0 4px 6px -1px rgba(${INK}, 0.10), 0 2px 4px -1px rgba(${INK}, 0.06)`,
        lg: `0 10px 15px -3px rgba(${INK}, 0.10), 0 4px 6px -2px rgba(${INK}, 0.05)`,
        xl: `0 20px 25px -5px rgba(${INK}, 0.10), 0 10px 10px -5px rgba(${INK}, 0.04)`,
        // 卡片專用：大模糊、低透明度、往下偏移，柔和且不搶戲。
        // 兩層疊加 —— 近距離那層界定卡片邊緣，遠距離那層做擴散暈開。
        card: `0 1px 2px rgba(${INK}, 0.04), 0 6px 16px -4px rgba(${INK}, 0.10)`,
    },
    fonts: {
        heading: '"Inter", sans-serif',
        body: '"Inter", sans-serif',
    },
    components: {
        Button: {
            baseStyle: {
                borderRadius: 'xl', // 圓角維持原樣，不動版面
                fontWeight: 'medium',
            },
            variants: {
                solid: (props) => {
                    // 舊版這裡無條件回傳 color: 'white'，導致
                    // <Button colorScheme="gray" variant="solid"> 變成淺灰底配白字（約 1.06:1，等於看不見）。
                    // 實例：ParentPickupPage 的「自行輸入」車牌按鈕，選中時就是這個狀態。
                    // 現在只有 colorScheme="brand" 才覆寫顏色，其餘交回 Chakra 預設。
                    if (props.colorScheme !== 'brand') return {}

                    // 原本主要動作用 brand.400（舊值 #38BDF8，白字只有 2.14:1，不合格）。
                    // 改用 brand.500（6.26:1）當主要動作色，hover / active 再往下一階。
                    return {
                        bg: 'brand.500',
                        color: 'white',
                        _hover: { bg: 'brand.600', _disabled: { bg: 'brand.500' } },
                        _active: { bg: 'brand.700' },
                    }
                },
            },
        },
        Card: {
            baseStyle: {
                container: {
                    borderRadius: '2xl', // 圓角維持原樣
                    // 依 user 要求恢復卡片陰影（柔和、大模糊、低透明度），讓米白卡片浮在黃畫布上。
                    // 有了陰影就不再需要外框線來界定邊緣，否則兩種手法疊加會顯得厚重。
                    boxShadow: 'card',
                    bg: 'white',
                },
            },
        },
        Table: {
            variants: {
                simple: {
                    th: {
                        // 表頭改用次要填色，讓「這是欄位標題不是資料」由表面編碼，
                        // 不必再靠加粗或加深文字。
                        bg: 'neutral.100',
                        borderColor: 'neutral.200',
                        color: 'neutral.500', // 對 neutral.100 = 5.37:1
                        textTransform: 'uppercase',
                        fontSize: 'xs',
                        letterSpacing: 'wider',
                    },
                    td: {
                        borderColor: 'neutral.200',
                        color: 'neutral.800', // 對 white = 14.26:1
                    },
                },
            },
        },
        // ── 以下四個是「真的浮起來」的東西：卡片表面 + 陰影 + 不靠邊框 ──
        Modal: {
            baseStyle: {
                overlay: { bg: `rgba(${INK}, 0.48)` }, // 與 Chakra 預設 blackAlpha.600 同透明度，改暖墨色
                dialog: { bg: 'white', boxShadow: 'lg' },
            },
        },
        Drawer: {
            baseStyle: {
                overlay: { bg: `rgba(${INK}, 0.48)` },
                dialog: { bg: 'white' },
            },
        },
        Menu: {
            baseStyle: {
                list: { bg: 'white', borderColor: 'neutral.200', boxShadow: 'lg' },
            },
        },
        Popover: {
            baseStyle: {
                content: { bg: 'white', borderColor: 'neutral.200', boxShadow: 'lg' },
            },
        },
        // 輸入框固定用卡片表面，讓「可以打字的地方」不論放在哪一層都認得出來
        Input: {
            variants: {
                outline: { field: { bg: 'white' } },
            },
        },
        Textarea: {
            variants: {
                outline: { bg: 'white' },
            },
        },
        Select: {
            variants: {
                outline: { field: { bg: 'white' } },
            },
        },
    },
    styles: {
        global: {
            body: {
                bg: 'canvas',   // 黃色畫布（不是 neutral.50，那是凹槽色）
                color: 'neutral.800',
            },
        },
    },
})

export default theme
